import React, { useState, useEffect, FC } from 'react';
import { DateTime } from 'luxon';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import { ClassicSpinner } from 'react-spinners-kit';

import { useI18n, useSelector } from '~Hooks';
import TableTable from './Components/TableTable';
import ReportTable from './Components/ReportTable';
import Calendar from './Components/Calendar';
import SearchBar from './Components/SearchBar';
import {
  COMPANY_QUERY,
  TABLE_EVENTS_QUERY,
  REPORT_EVENTS_QUERY,
  TAB_TYPE,
  TABLE_EVENTS_HEADERS,
  REPORT_EVENTS_HEADERS,
  TABLE_SEARCH_KEYS,
  REPORT_SEARCH_KEYS,
} from './constants';

import { Wrapper, Top, Right, Content, StateWrapper, Tabs, Tab } from './styles';
import { buildSearchableReportEvents, buildSearchableTableEvents } from './utils';
import { JournalCompany, JournalCompany_company, JournalCompanyVariables } from './__queries__';

const EJournal: FC = () => {
  const location = useLocation();
  const [, , venueId] = location.pathname.split('/');
  const { i18n } = useI18n();

  // Fetch company
  const companyId = useSelector((state) => state.company!);
  const tables = useSelector((state) => state.tables);
  const staff = useSelector((state) => state.companyStaff)?.staffs || [];

  const { data: dataCompany } = useQuery<JournalCompany, JournalCompanyVariables>(COMPANY_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      companyId,
    },
  });

  const [company, setCompany] = useState<JournalCompany_company>();
  useEffect(() => {
    if (dataCompany) {
      setCompany(dataCompany?.company);
    }
  }, [dataCompany]);

  const [eventType, setEventType] = useState(TAB_TYPE.TABLE);

  const currentDate = new Date();
  const [chosenDate, setChosenDate] = useState<Date>(currentDate);

  const [getTableEvents, { data: dataTable, loading: loadingTable }] = useLazyQuery(
    TABLE_EVENTS_QUERY,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [getReportEvents, { data: dataReport, loading: loadingReport }] = useLazyQuery(
    REPORT_EVENTS_QUERY,
    {
      fetchPolicy: 'network-only',
    },
  );

  const loading = loadingTable || loadingReport;
  const [searchResult, setResarchResult] = useState<any>([]);

  // fetch events by startDate
  useEffect(() => {
    const sentAfter = DateTime.fromJSDate(chosenDate)
      .startOf('day')
      .toISO();

    const variables = { venueId, sentAfter };

    if (eventType === TAB_TYPE.TABLE) {
      getTableEvents({ variables });
    } else {
      getReportEvents({ variables });
    }
  }, [chosenDate, eventType]);

  // filter events by endDate
  const filterEventsByChosenDay = (events: any = []) => {
    const endDate = DateTime.fromJSDate(chosenDate).endOf('day').startOf('second');
    return events.filter((event: any) =>
      DateTime.fromISO(event.createdAt).startOf('second') < endDate,
    );
  };

  // Get table events
  const [tableEvents, setTableEvents] = useState<any>([]);
  useEffect(() => {
    if (dataTable && dataCompany) {
      const events = dataTable?.tableEvents?.items?.filter(
        (event: any) => event.type !== 'ADD_LOG',
      );
      const filteredList = filterEventsByChosenDay(events);
      const tList = buildSearchableTableEvents(filteredList, staff, tableEvents);
      setTableEvents(tList);
    }
  }, [dataTable, dataCompany]);

  // Get report events
  const [reportEvents, setReportEvents] = useState<any>([]);
  useEffect(() => {
    if (dataReport && dataCompany) {
      const events = dataReport?.reportEvents?.items;
      const filteredList = filterEventsByChosenDay(events);
      const rList = buildSearchableReportEvents(filteredList);
      setReportEvents(rList);
    }
  }, [dataReport, dataCompany]);

  const searchList = eventType === TAB_TYPE.TABLE ? tableEvents : reportEvents;
  const searchKeys = eventType === TAB_TYPE.TABLE ? TABLE_SEARCH_KEYS : REPORT_SEARCH_KEYS;

  return (
    <Wrapper>
      <Top>
        <Calendar
          onChange={(date: Date) => setChosenDate(date)}
          value={chosenDate}
          maxDate={currentDate}
        />
        <Right>
          <SearchBar list={searchList} keys={searchKeys} setSearchResult={setResarchResult} />
          <Tabs>
            <Tab
              onClick={() => setEventType(TAB_TYPE.TABLE)}
              active={eventType === TAB_TYPE.TABLE}>
              {i18n('EJournal.TableEvents')}
            </Tab>
            <Tab
              onClick={() => setEventType(TAB_TYPE.REPORT)}
              active={eventType === TAB_TYPE.REPORT}>
              {i18n('EJournal.ReportEvents')}
            </Tab>
          </Tabs>
        </Right>
      </Top>

      <Content>
        {loading && (
          <StateWrapper>
            <ClassicSpinner color="#1c232c" size={60} loading />
          </StateWrapper>
        )}

        {!loading && searchResult.length < 1 && (
          <StateWrapper>{i18n('EJournal.NoEventsFound')}</StateWrapper>
        )}

        {!loadingTable && !!searchResult.length && eventType === TAB_TYPE.TABLE && (
          <TableTable
            headers={TABLE_EVENTS_HEADERS}
            staff={staff}
            items={company?.items || []}
            tables={tables}
            events={searchResult}
          />
        )}

        {!loadingReport && !!searchResult.length && eventType === TAB_TYPE.REPORT && (
          <ReportTable headers={REPORT_EVENTS_HEADERS} events={searchResult} />
        )}
      </Content>
    </Wrapper>
  );
};

export default EJournal;
